import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { TrendingUp, Sparkles, Loader2, ArrowLeft, ArrowRight, Zap } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreditBoostGenerator = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [simResults, setSimResults] = useState(null);
  
  const [profile, setProfile] = useState({
    current_score: null,
    score_range: 'fair',
    credit_cards: 2,
    total_debt: '',
    late_payments: 0,
    collections: 0,
    credit_age: '',
    hard_inquiries: 0,
    goals: [],
  });

  const [planType, setPlanType] = useState('starter');

  const goals = [
    'Buy a house',
    'Get a car loan',
    'Qualify for better credit cards',
    'Lower insurance rates',
    'Rent an apartment',
    'Get a job (credit check)',
    'Just improve my score',
  ];

  const handleChange = (field, value) => {
    setProfile({ ...profile, [field]: value });
  };

  const toggleGoal = (goal) => {
    const newGoals = profile.goals.includes(goal)
      ? profile.goals.filter(g => g !== goal)
      : [...profile.goals, goal];
    setProfile({ ...profile, goals: newGoals });
  };

  const handleSimulate = async () => {
    setSimulating(true);
    try {
      const token = localStorage.getItem('careerboost_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/creditboost/simulate`,
        profile,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSimResults(response.data);
    } catch (error) {
      toast.error('Failed to run simulation');
    } finally {
      setSimulating(false);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('careerboost_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/creditboost/action-plan`,
        { profile, plan_type: planType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Action plan generated!');
      navigate(`/creditboost/plans/${response.data.plan_id}`);
    } catch (error) {
      toast.error('Failed to generate plan');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => setStep(step + 1);
  const prevStep = () => setStep(step - 1);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/creditboost/dashboard')} data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  CreditBoost
                </span>
              </div>
            </div>
            <div className="text-sm text-slate-500">
              Step {step} of 3
            </div>
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-600 transition-all duration-300"
              style={{ width: `${(step / 3) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-12">
        {step === 1 && (
          <Card data-testid="step-1">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                Let's assess your credit situation
              </CardTitle>
              <CardDescription>
                The more accurate you are, the better your action plan will be
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>What's your current credit score range?</Label>
                <Select
                  value={profile.score_range}
                  onValueChange={(value) => handleChange('score_range', value)}
                >
                  <SelectTrigger data-testid="select-score-range">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="poor">Poor (300-579)</SelectItem>
                    <SelectItem value="fair">Fair (580-669)</SelectItem>
                    <SelectItem value="good">Good (670-739)</SelectItem>
                    <SelectItem value="excellent">Excellent (740+)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Exact score (if you know it)</Label>
                <Input
                  type="number"
                  value={profile.current_score || ''}
                  onChange={(e) => handleChange('current_score', parseInt(e.target.value) || null)}
                  placeholder="e.g., 620"
                  min={300}
                  max={850}
                  data-testid="input-score"
                />
              </div>

              <div className="space-y-2">
                <Label>How many credit cards do you have?</Label>
                <Select
                  value={String(profile.credit_cards)}
                  onValueChange={(value) => handleChange('credit_cards', parseInt(value))}
                >
                  <SelectTrigger data-testid="select-cards">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">None</SelectItem>
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="2">2-3</SelectItem>
                    <SelectItem value="4">4-5</SelectItem>
                    <SelectItem value="6">6+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Total credit card debt (approximate)</Label>
                <Input
                  value={profile.total_debt}
                  onChange={(e) => handleChange('total_debt', e.target.value)}
                  placeholder="e.g., $5,000"
                  data-testid="input-debt"
                />
              </div>

              <Button onClick={nextStep} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="next-1">
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card data-testid="step-2">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                Negative items on your credit report
              </CardTitle>
              <CardDescription>
                These have the biggest impact on your score
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Late payments (30+ days) in last 2 years</Label>
                <Select
                  value={String(profile.late_payments)}
                  onValueChange={(value) => handleChange('late_payments', parseInt(value))}
                >
                  <SelectTrigger data-testid="select-late">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">None</SelectItem>
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="2">2-3</SelectItem>
                    <SelectItem value="4">4-5</SelectItem>
                    <SelectItem value="6">6+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Collections accounts</Label>
                <Select
                  value={String(profile.collections)}
                  onValueChange={(value) => handleChange('collections', parseInt(value))}
                >
                  <SelectTrigger data-testid="select-collections">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">None</SelectItem>
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="2">2-3</SelectItem>
                    <SelectItem value="4">4+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Hard inquiries in last 2 years</Label>
                <Select
                  value={String(profile.hard_inquiries)}
                  onValueChange={(value) => handleChange('hard_inquiries', parseInt(value))}
                >
                  <SelectTrigger data-testid="select-inquiries">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0-1</SelectItem>
                    <SelectItem value="2">2-3</SelectItem>
                    <SelectItem value="4">4-5</SelectItem>
                    <SelectItem value="6">6+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Length of credit history</Label>
                <Select
                  value={profile.credit_age}
                  onValueChange={(value) => handleChange('credit_age', value)}
                >
                  <SelectTrigger data-testid="select-age">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="less1">Less than 1 year</SelectItem>
                    <SelectItem value="1-2">1-2 years</SelectItem>
                    <SelectItem value="3-5">3-5 years</SelectItem>
                    <SelectItem value="5+">5+ years</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-4">
                <Button variant="outline" onClick={prevStep} className="flex-1" data-testid="prev-2">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={nextStep} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="next-2">
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card data-testid="step-3">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                Your goals & plan selection
              </CardTitle>
              <CardDescription>
                What do you want to achieve with better credit?
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label>Select your goals (select all that apply)</Label>
                <div className="grid grid-cols-2 gap-3">
                  {goals.map((goal) => (
                    <div
                      key={goal}
                      onClick={() => toggleGoal(goal)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        profile.goals.includes(goal)
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <span className="text-sm">{goal}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Label>Choose your plan</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div
                    onClick={() => setPlanType('starter')}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      planType === 'starter'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200'
                    }`}
                  >
                    <p className="font-semibold text-slate-900">Quick Fix</p>
                    <p className="text-2xl font-bold text-blue-600 my-1">$49</p>
                    <p className="text-xs text-slate-500">30-day roadmap</p>
                  </div>
                  <div
                    onClick={() => setPlanType('complete')}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      planType === 'complete'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200'
                    }`}
                  >
                    <p className="font-semibold text-slate-900">Complete</p>
                    <p className="text-2xl font-bold text-blue-600 my-1">$99</p>
                    <p className="text-xs text-slate-500">90-day transformation</p>
                  </div>
                </div>
              </div>

              {/* Score Simulation */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-medium text-slate-900">Score Simulator</p>
                    <p className="text-sm text-slate-500">See your potential improvement</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSimulate}
                    disabled={simulating}
                    data-testid="simulate-btn"
                  >
                    {simulating ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-1" />
                        Simulate
                      </>
                    )}
                  </Button>
                </div>
                {simResults && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-slate-500">Estimated Current</span>
                      <span className="font-bold text-slate-900">{simResults.current_estimated_score}</span>
                    </div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-slate-500">Potential Score</span>
                      <span className="font-bold text-blue-600">{simResults.potential_score_range}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Timeframe</span>
                      <span className="text-sm text-slate-700">{simResults.timeframe}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-4">
                <Button variant="outline" onClick={prevStep} className="flex-1" data-testid="prev-3">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={handleGenerate}
                  disabled={loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="generate-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Get My Plan
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CreditBoostGenerator;
